import { sponsorTypeToWire } from '@/scheduling/lib/sponsor-type-wire';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { cssColorToFlutter } from '@/shared/lib/flutter-color';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type {
  CreateAppointmentRequest,
  CreateAppointmentProfileRequest,
  FlagResponse,
  LocationModel,
  ResidentialAddress,
  ReminderProfileFull,
  SpecialityWire,
} from '@/types/scheduling-api';

// Brand-primary in Flutter `Color(int)` ARGB form (mirrors
// --color-brand-primary #005FD4). MUST be ARGB, not CSS hex: Flutter renders
// the appointment card with `Color(int.tryParse(color)!)`, which throws on a
// `#RRGGBB` value and gray-boxes the appointment. See cssColorToFlutter.
const DEFAULT_PROFILE_COLOR = '0xFF005FD4';

/**
 * Form values → BookAppointment payload.
 *
 * The wire shape matches Flutter's `Appointment.toJson()` exactly — the
 * dev curl in 2026-05-08 troubleshooting confirmed every field name and
 * casing. Earlier we shipped a much sparser body which the backend
 * accepted on POST but couldn't enrich on the next read (gRPC
 * `GetSpecialitiesByCodesAsync` crashed when looking up our hardcoded
 * "general" code), so the calendar refresh 500ed after a successful
 * booking. This transform fixes that by:
 *
 *   - shipping the picked provider's SNOMED `specialty` (not "general")
 *   - shipping the full `appointmentProfile` snapshot (id / specialityId
 *     / billingProfileId / roomId / inAppBooking / isPreferred / …)
 *   - shipping the full `reminderProfile` with its `reminderProfileType`
 *     rules array
 *   - sending payment totals as `invoice` (Flutter's field name) instead
 *     of `billingTotal`
 *   - using `sponsorType: "Self-pay"` (lowercase p, Flutter parity)
 *   - flag entries as `{ code: number, display, categoryName: null }`
 *   - shipping the residential address object on Home visits
 *
 * Single most critical file in this domain — every field traces to its
 * Flutter source.
 */
export function transformFormToRequest(
  form: AppointmentFormValues,
  context: {
    organizationId: string;
    practiceId: string;
    /** Default room when the picked profile carries none. Empty string omits. */
    roomId: string;
    createdBy: string;
  },
): CreateAppointmentRequest {
  const picked = (form.pickedProfile ?? null) as
    | (CreateAppointmentProfileRequest & {
        roomId?: string;
        billingProfileId?: string;
        specialityId?: string;
        speciality?: SpecialityWire;
        location?: { id: string; name: string };
        referenceId?: string;
        isPreferred?: boolean;
        isDefault?: boolean;
        isUpdated?: boolean;
        inAppBooking?: boolean;
      })
    | null;

  // appointmentType — Flutter ships `{ code, definition: null, display, system: null }`.
  const appointmentType = form.appointmentType
    ? {
        code: form.appointmentType.code,
        display: form.appointmentType.display,
        definition: form.appointmentType.definition ?? null,
        system: null,
      }
    : { code: 'ROUTINE', display: 'Routine', definition: null, system: null };

  const reasonCode = form.reasonCode
    ? {
        code: (form.reasonCode.code ?? '').toLowerCase(), // dev ships lowercase UUID
        display: form.reasonCode.display ?? '',
        definition: form.reasonCode.definition ?? '',
        system: null,
      }
    : { code: '', display: '', definition: '', system: null };

  // Speciality — picked from the provider; falls back to the picked
  // profile's speciality when the provider didn't carry one. Both shapes
  // are SNOMED-formatted (Flutter wire shape).
  const specialty: SpecialityWire =
    (form.providerSpecialityCode as SpecialityWire | null) ??
    picked?.speciality ?? {
      specialitySkillId: undefined,
      code: undefined,
    };
  const specialityId =
    (form.providerSpecialityCode as SpecialityWire | null)?.specialitySkillId ??
    picked?.specialityId;

  // Sponsor casing — form enum "Self-Pay" → wire "Self-pay" (lowercase p).
  // Centralised in sponsor-type-wire.ts (round-trip tested).
  const sponsorTypeWire = sponsorTypeToWire(form.sponsorType);

  // Flag list — dev ships `{ code: number, display, categoryName: null }`.
  // We may have either a richer `patientFlags` list (auto-populated from
  // PreBook) or just `flagIds` strings.
  const flagList: FlagResponse[] =
    form.patientFlags.length > 0
      ? form.patientFlags.map((f) => ({
          code: typeof f.code === 'number' ? f.code : Number(f.code) || 0,
          display: f.display ?? '',
          categoryName: f.categoryName ?? null,
        }))
      : form.flagIds.map((id) => ({
          code: Number(id) || 0,
          display: '',
          categoryName: null,
        }));

  // Reminder profile — full object Flutter ships on Book.
  const reminderProfile: ReminderProfileFull | undefined =
    form.reminderProfile?.id || form.reminderProfileId
      ? {
          id: form.reminderProfile?.id ?? form.reminderProfileId ?? undefined,
          organizationId: context.organizationId,
          practiceId: context.practiceId,
          practitionerId: '',
          profileName:
            form.reminderProfile?.profileName ??
            form.reminderProfileName ??
            undefined,
          customNote: form.reminderProfile?.customNote,
          enableInAppBooking: form.reminderProfile?.enableInAppBooking,
          isDefault: form.reminderProfile?.isDefault,
          isUpdated: false,
          reminderProfileType: form.reminderProfile?.reminderProfileType?.map((r) => ({
            id: r.id,
            type: r.type,
            time: r.time,
            whenToSend: r.whenToSend,
            number: r.number,
          })),
        }
      : undefined;

  // appointmentProfile — full snapshot. Backend reads many ids from this
  // object, so we ship as much as we captured on pick.
  const profileColor = picked?.color ?? form.appointmentProfileColor ?? undefined;
  const appointmentProfile: CreateAppointmentRequest['appointmentProfile'] = {
    id: picked?.id ?? form.appointmentProfileId ?? undefined,
    organizationId: context.organizationId,
    practiceId: context.practiceId,
    practionerId: form.providerId!,
    // Normalise to Flutter ARGB — a CSS `#RRGGBB` here crashes the Flutter
    // calendar card (int.tryParse → null → throw → gray box).
    color: cssColorToFlutter(profileColor) ?? DEFAULT_PROFILE_COLOR,
    appointmentType,
    // Profile-scoped reasonCode mirrors the root reasonCode but Flutter
    // also includes `system: null` here.
    reasonCode: form.reasonCode
      ? {
          code: (form.reasonCode.code ?? '').toLowerCase(),
          display: form.reasonCode.display ?? '',
          definition: form.reasonCode.definition ?? '',
          system: null,
        }
      : { code: '', display: '', definition: '', system: null },
    profileName: picked?.profileName ?? form.appointmentProfileName ?? '',
    location: picked?.location ?? form.location ?? undefined,
    minutesDuration: picked?.minutesDuration ?? form.minutesDuration ?? undefined,
    speciality: picked?.speciality ?? specialty,
    specialityId: picked?.specialityId ?? specialityId,
    roomId: picked?.roomId ?? undefined,
    billingProfileId: picked?.billingProfileId ?? form.billingProfileId ?? undefined,
    inAppBooking: picked?.inAppBooking ?? false,
    isDefault: picked?.isDefault ?? false,
    isUpdated: false,
    referenceId: picked?.referenceId ?? picked?.id ?? undefined,
    isPreferred: picked?.isPreferred ?? false,
    // The structural mismatch between the appointment-profile fixture
    // (reasonCode with `system: null`, picked optional unions) and
    // CreateAppointmentRequest['appointmentProfile'] doesn't admit a
    // direct cast — go through `unknown` per TS's hint.
  } as unknown as CreateAppointmentRequest['appointmentProfile'];

  // Residential address — full object on Home visits (Flutter parity).
  const residentialAddress = form.residentialAddress as ResidentialAddress | null;

  // Invoice — dev's wire field; we previously sent this as `billingTotal`.
  const invoice = {
    organization: null,
    id: null,
    providerName: null,
    resubmissionAttachmentFileUrl: null,
    resubmissionAttachmentFileName: null,
    patientId: null,
    providerId: null,
    practiceId: null,
    resubmissionComment: null,
    invoiceNumber: null,
    invoiceStatus: null,
    policyNo: null,
    sponsorType: null,
    sponsor: null,
    provider: null,
    appointmentId: null,
    trnNumber: null,
    encounterId: null,
    locationId: null,
    encounterNumber: null,
    managementDiscount: null,
    invoiceDate: null,
    draftedOn: null,
    encounterDate: null,
    patient: null,
    practice: null,
    claim: null,
    diagnosisList: null,
    services: null,
    note: null,
    grossTotal: form.invoiceTotals?.grossTotal ?? 0,
    discountTotal: form.invoiceTotals?.discountTotal ?? 0,
    netTotal: form.invoiceTotals?.netTotal ?? 0,
    patientPayable: form.invoiceTotals?.patientPayable ?? 0,
    deposited: 0,
    sponsorOutstanding: form.invoiceTotals?.sponsorOutstanding ?? 0,
    sponsorPaymentTotal: 0,
    patientPaymentStatus: 0,
    patientOutstanding: 0,
    excludedTotal: 0,
    patientShareTotal: 0,
    sponsorAmountTotal: 0,
    eligibilityEndDate: null,
    eligibilityStartDate: null,
    errors: null,
    assessmentPlan: null,
  };

  // Top-level `location` wire-shape — Flutter ships the FULL location
  // object (id + name + imageUrl + isArchived + description). Source
  // priority:
  //   1. form.location (if has real id) — set by LocationSelector
  //      auto-pick or user click, may lack imageUrl/description so
  //      we enrich from the picked profile's location when available.
  //   2. picked.location (if a profile was selected) — the picked
  //      profile always carries a fully-shaped location.
  //   3. neither set → null (let .NET reject with a useful error).
  //
  // The prior `{id:'', name:''}` empty-string fallback caused 400s
  // (.NET binder rejects `""` for `System.Guid`). Now we fall through
  // to the picked profile's location instead, which mirrors Flutter
  // (profile pick always implies a location).
  const pickedLocation = picked?.location as
    | {
        id?: string;
        name?: string;
        imageUrl?: string;
        isArchived?: boolean;
        description?: string;
      }
    | undefined;
  const formLocationHasId = !!form.location?.id;
  const resolvedLocation: LocationModel | null = formLocationHasId
    ? {
        id: form.location!.id,
        name: form.location!.name,
        imageUrl: pickedLocation?.imageUrl,
        isArchived: pickedLocation?.isArchived ?? false,
        description: pickedLocation?.description ?? '',
      }
    : pickedLocation?.id
      ? {
          id: pickedLocation.id,
          name: pickedLocation.name ?? '',
          imageUrl: pickedLocation.imageUrl,
          isArchived: pickedLocation.isArchived ?? false,
          description: pickedLocation.description ?? '',
        }
      : null;

  return {
    location: resolvedLocation ?? ({} as LocationModel),
    organizationId: context.organizationId,
    practiceId: context.practiceId,
    // Service-category / service-type fields — corrected 2026-05-22
    // against the dev curl. The prior hardcoded values
    // (`serviceCategoryId: 1`, `serviceTypeId: 1`,
    //  `serviceCategory: {code:1, display:"Adoption"}`,
    //  `serviceType: {code:1, display:"Adoption/Permanent Care Info/Support"}`)
    // were a placeholder defensive against a since-fixed 500-on-
    // absence backend bug. The dev curl now ships explicit `null` for
    // all four — wire-shape parity restored, and we no longer land
    // bogus "Adoption" category metadata on every appointment.
    serviceCategoryId: null,
    serviceTypeId: null,
    specialityId,
    roomId: picked?.roomId
      ? picked.roomId
      : context.roomId && context.roomId.length > 0
        ? context.roomId
        : undefined,
    isBlock: null,
    recurringId: null,
    serviceCategory: null,
    serviceType: null,
    specialty,
    appointmentType,
    reasonCode,
    priority: form.priority,
    description: form.description ? form.description : null,
    patientId: form.patientId!,
    practitionerId: form.providerId!,
    paymentLink: form.paymentLink ?? null,
    // Normalise to the backend's no-Z local-clock shape (YYYY-MM-DDTHH:mm:ss.sss)
    // — the backend applies the practice/user timezone itself, so the wire value
    // must be the literal local time, never a UTC `Z` instant. Mirrors
    // `useRescheduleAppointment` (which already re-normalises its dates). Without
    // this, a Z-suffixed slot source (e.g. a custom-time pick) shipped a
    // UTC-shifted time that the backend re-converted to the wrong slot.
    // `|| undefined` — formatLocalDatetimeForWire returns '' for an unparseable
    // value, and an empty-string date binds to NULL on the backend (previously a
    // NodaTime 500, now a SlotRequired 400). Never ship '' as a date.
    start: (form.slotStart && formatLocalDatetimeForWire(form.slotStart)) || undefined,
    end: (form.slotEnd && formatLocalDatetimeForWire(form.slotEnd)) || undefined,
    minutesDuration: form.minutesDuration ?? undefined,
    comment: form.comment ?? null,
    patientInstruction: null,
    updatedBy: null,
    createdBy: context.createdBy,
    sponsorType: sponsorTypeWire,
    policyNo: form.policyNo ?? null,
    residentialAddressId: form.residentialAddressId ?? undefined,
    callBackContractId: form.callbackContactId ?? '',
    residentialAddress: residentialAddress ?? undefined,
    createdPerson: null,
    updatedPerson: null,
    appointmentProfile,
    flagList,
    questionnaireFormId: null,
    reminderProfile,
    isCreatedByPatient: false,
    questionnaireForm: form.questionnaireId
      ? { questionnaireId: form.questionnaireId, name: form.questionnaireName ?? '' }
      : null,
    referenceAppointmentId: form.referenceAppointmentId ?? null,
    // FU request id — populated by the FU "Create Follow Up" flow
    // (frame-scheduling.tsx#handleFuCreateFollowUp). Mirrors Flutter
    // `calendar_screen.dart:179` `followUpId: request.id`. Lets the
    // backend link the saved appointment back to its originating FU
    // request. Null on every other create path.
    followUpId: form.followUpId ?? null,
    isRelatedPerson: form.isRelatedPerson,
    questionnaire: null,
    patientAppointmentPayerDetail: null,
    invoice,
    // Sponsor — Flutter parity: full payer (incl. payerCompanyId + relatedTpa)
    // for insurance/corporate, null for self-pay. Shipping a sparse sponsor
    // (no payerCompanyId) makes the backend store an incomplete invoice sponsor
    // and breaks eRx→DHPO (empty ReceiverID/PayerID).
    sponsor:
      sponsorTypeWire === 'Self-pay' || !form.sponsor?.id
        ? null
        : {
            id: form.sponsor.id,
            name: form.sponsor.payerName ?? form.sponsor.name ?? '',
            payerName: form.sponsor.payerName,
            imageUrl: form.sponsor.imageUrl,
            payerCompanyId: form.sponsor.payerCompanyId,
            relatedTpa: form.sponsor.relatedTpa,
          },
    // Recurrence wire shape (PROD-961 — fix for the Book NPE chain).
    // Match Flutter's `recurrence_properties_model.dart:98-111`
    // verbatim:
    //   - `id` is always emitted (null on create, real GUID on edit).
    //     .NET deserializer treats absent-vs-present-null differently
    //     on this command path.
    //   - `days` is `null` OUTSIDE 'Weeks' period — Flutter helper:
    //     `(period == weekPeriod) ? days : null`. Shipping `days: []`
    //     for Days/Months/Years triggered the NPE in
    //     ConvertCreateAppointmentCommandTimeToUtc.
    //   - `slots` is the full AppointmentSlot shape from
    //     POST /api/Appointment/RetrieveSlots (carries appointmentType,
    //     practiceId, providerId, overbooked, comment, practiceName).
    //     The picker's onSave handler now forwards all of these.
    recurring: form.recurring
      ? {
          id: null,
          startDate: form.recurring.startDate,
          endDate: form.recurring.endDate,
          repeatEvery: form.recurring.repeatEvery,
          numberOfRepeat: form.recurring.numberOfRepeat,
          period: form.recurring.period,
          days:
            form.recurring.period === 'Weeks'
              ? form.recurring.days ?? []
              : null,
          slots: form.recurring.slots ?? [],
        }
      : null,
    editRecurring: null,
    appointmentStatus: null,
    addedServices: [],
    addedIndividualServices: [],
  };
}
